import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../hooks/useAxios";
import "../UpdateQuestionPage/UpdateQuestionPage.css"; 
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import CustomInputField from "../../../components/CustomInputFields/CustomInputField/CustomInputField";
import Header from "../../../components/Header/Header";
const navLinks = [];

function CreateQuestionPage() {
  const { quizId } = useParams();
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [choices, setChoices] = useState([]);
  const [error, setError] = useState(null);
  const [isMathBody, setIsMathBody] = useState(false);

  const navigate = useNavigate();
  const axiosInstance = useAxios();

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setImageFile(file); 
      const previewURL = URL.createObjectURL(file);
      setImage(previewURL);
    }
  };

  const handleDeleteImage = () => {
    if (imageFile && image?.startsWith("blob:")) {
      URL.revokeObjectURL(image);
    }
    setImage(null);
    setImageFile(null);
  };

  const handleChoiceChange = (index, value) => {
    const updatedChoices = [...choices];
    updatedChoices[index].value = value;
    setChoices(updatedChoices);
  };

  const handleAddChoice = () => {
    const nextChoiceNumber = String.fromCharCode(65 + choices.length); // A, B, C...
    setChoices([...choices, { number: nextChoiceNumber, value: "" }]);
  };

  const handleDeleteChoice = (index) => {
    const updatedChoices = choices.filter((_, i) => i !== index);
    const renumberedChoices = updatedChoices.map((choice, i) => ({
      ...choice,
      number: String.fromCharCode(65 + i),
    }));
    setChoices(renumberedChoices);
  };

  const handleSave = async () => {
    setError(null);

    const formData = new FormData();
    formData.append("body", body);
    // For simplicity, we won't require correct_answer or correct_choice_number initially.
    // You can add inputs for them if needed.
    // If needed:
    // formData.append("correct_answer", correctAnswer);
    // formData.append("correct_choice_number", correctChoiceNumber);

    if (imageFile) {
      formData.append("image", imageFile);
    }

    if (choices.length > 0) {
        formData.append("choices", JSON.stringify(choices));
      }
      

    try {
      const response = await axiosInstance.post(
        `/quizzes/${quizId}/questions/create`,
        formData
      );

      if (response.data.success) {
        navigate(`/quiz-review/${quizId}`);
      } else {
        setError(response.data.message || "Failed to create the question.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBodyToggleChange = (event) => {
    setIsMathBody(event.target.checked);
  };

  const handleChoiceModeToggle = (index) => {
    const updatedChoices = [...choices];
    updatedChoices[index].isMathMode = !updatedChoices[index].isMathMode;
    setChoices(updatedChoices);
  };

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Box className="update-question-container">
        <Typography variant="h4" className="page-title">
          Create Question
        </Typography>

        {error && (
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
        )}

        <Card className="form-card">
          <CardContent>
          <ToggleSwitch
              mainLabel="question body mode"
              labelLeft="Regular"
              labelRight="Math"
              checked={isMathBody}
              onChange={handleBodyToggleChange}
            />
            { isMathBody ?
            <CustomInputField
                body={body}
                onBodyChange={(value) => setBody(value)}
                label={"Question Body"}
              />
            :
            <TextField
              label="Question Body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              multiline
              rows={3}
              className="custom-text-field"
              />
            }      

            <Box className="image-container">
              {image ? (
                <Box className="image-preview">
                  <img
                    src={image}
                    alt="Question"
                    className="preview-image"
                  />
                  <Box className="image-actions">
                    <IconButton
                      color="primary"
                      onClick={() => document.getElementById("image-input").click()}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={handleDeleteImage}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Button
                  className="upload-button text-button"
                  onClick={() => document.getElementById("image-input").click()}
                  startIcon={<AddPhotoAlternateIcon />}
                >
                  Upload Image
                </Button>
              )}
              <input
                type="file"
                id="image-input"
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleImageChange}
              />
            </Box>

            <Box className="choices-container">
              <Typography variant="h6" className="choices-title">
                Choices
              </Typography>
              {choices.map((choice, index) => (
                <>
                    <ToggleSwitch
                      mainLabel={`Mode`}
                      labelLeft="Regular"
                      labelRight="Math"
                      checked={choice.isMathMode}
                      onChange={() => handleChoiceModeToggle(index)}
                    />
                  <Box key={index} className="choice-row" display="flex" alignItems="center">
                    <Typography className="choice-number">{choice.number}.</Typography>
                    <Box flexGrow={1}>
                      {choice.isMathMode ? (
                        <CustomInputField
                          body={choice.value}
                          onBodyChange={(value) => {
                            const updatedChoices = [...choices];
                            updatedChoices[index].value = value;
                            setChoices(updatedChoices);
                          }}
                          label={`Choice ${choice.number}`}
                        />
                      ) : (
                        <TextField
                          value={choice.value}
                          onChange={(e) => {
                            const updatedChoices = [...choices];
                            updatedChoices[index].value = e.target.value;
                            setChoices(updatedChoices);
                          }}
                          fullWidth
                        />
                      )}
                    </Box>
                    
                    <IconButton
                      color="error"
                      onClick={() => {
                        const updatedChoices = choices.filter((_, i) => i !== index);
                        setChoices(
                          updatedChoices.map((choice, i) => ({
                            ...choice,
                            number: String.fromCharCode(65 + i), // Renumber choices dynamically
                          }))
                        );
                      }}
                      aria-label="delete-choice"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </>
              ))}
              <Button
                className="add-choice-button text-button"
                onClick={handleAddChoice}
              >
                <AddIcon className="add-icon" />
                Add Choice
              </Button>
            </Box>

            <Grid container spacing={2} justifyContent="center" className="actions-container">
              <Grid item>
                <Button onClick={handleSave} className="primary-button">
                  Save
                </Button>
              </Grid>
              <Grid item>
                <Button onClick={handleCancel} className="secondary-button">
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}

export default CreateQuestionPage;
